import type { ColumnId } from '~/_shared/types/spreadsheetData/spreadsheetColumn';
import type { SpreadsheetId } from '~/_shared/types/spreadsheetData/spreadsheetRow';
import { apiPost } from '~/_shared/utils/api/api.helpers';
import type { Uuid } from '~/_shared/utils/createUuid';
import type { RealSpreadsheetVersionId } from '~/spreadsheet/spreadsheet.repository';
import type { BoundaryGroupId } from '../boundaries/boundaryIdentifier.type';
import type { ChartMetricType } from '../mapSettings/data/charts/mapSettingsCharts.types';

export enum StatsProcessingStatus {
  InProgress = 'InProgress',
  Finished = 'Finished'
}

type BoundaryStatsRequestItemBase = Readonly<{
  query_id: Uuid;
  boundary_group_id: BoundaryGroupId;
  type: ChartMetricType;
}>;

type BoundaryStatsSpreadsheetColumnRequestItem = BoundaryStatsRequestItemBase & Readonly<{
  column_value?: string;
  metric: ColumnId | null;
  spreadsheet_id: SpreadsheetId;
  type: ChartMetricType.SpreadsheetColumn | ChartMetricType.LocationCount;
}>;

type BoundaryStatsDemographicRequestItem = BoundaryStatsRequestItemBase & Readonly<{
  type: ChartMetricType.Demographic;
  metric: string;
}>;

export type BoundaryStatsRequestItem =
  BoundaryStatsSpreadsheetColumnRequestItem
  | BoundaryStatsDemographicRequestItem;

type BoundaryStatsResponseItemBase = Readonly<{
  id: number;
  computed_at: string; // UTC/ISO datetime with timezone
  boundary_group_id: BoundaryGroupId;
  boundary_group_timestamp: number;
  data?: Readonly<{ // when StatsProcessingStatus == 'Finished'
    median: number;
    avg: number;
    values: {
      [boundary_id: string]: number;
    };
  }>;
  status: StatsProcessingStatus;
}>;

type BoundaryStatsSpreadsheetColumnResponseItem = BoundaryStatsResponseItemBase & Readonly<{
  type: ChartMetricType.SpreadsheetColumn;
  metric: ColumnId;
  spreadsheet_id: SpreadsheetId;
  spreadsheet_version: Readonly<{
    [realSpreadsheetId: string]: RealSpreadsheetVersionId;
  }>;
}>;

type BoundaryStatsLocationCountResponseItem = BoundaryStatsResponseItemBase & Readonly<{
  type: ChartMetricType.LocationCount;
  metric: ColumnId | null; // undefined for total location count, columnId for group columns
  column_value: string | null; // if notUndefined(metric) && isUndefined(column_value) => the value is empty ('')
  spreadsheet_id: SpreadsheetId;
  spreadsheet_version: Readonly<{
    [realSpreadsheetId: string]: RealSpreadsheetVersionId;
  }>;
}>;

type BoundaryStatsDemographicResponseItem = BoundaryStatsResponseItemBase & Readonly<{
  type: ChartMetricType.Demographic;
  metric: string; // demographic id
}>;

export type BoundaryStatsResponseItem =
  | BoundaryStatsSpreadsheetColumnResponseItem
  | BoundaryStatsLocationCountResponseItem
  | BoundaryStatsDemographicResponseItem;

export type BoundaryStatsResponse = Readonly<{
  result: {
    [queryId: Uuid]: BoundaryStatsResponseItem | null;
  };
}>;

export const getBoundaryStats = (
  clientId: number,
  queryItems: ReadonlyArray<BoundaryStatsRequestItem>
): Promise<BoundaryStatsResponse> => {
  const requestUrl = `/api/clients/${clientId}/charts/boundary-statistics`;
  return apiPost(requestUrl, {
    query: queryItems,
  });
};

export const calculateBoundaryStats = (
  clientId: number,
  queryItems: ReadonlyArray<BoundaryStatsRequestItem>
): Promise<BoundaryStatsResponse> => {
  const requestUrl = `/api/clients/${clientId}/charts/boundary-statistics/calculate`;
  return apiPost(requestUrl, {
    query: queryItems,
  });
};
